import React from 'react';
import { motion } from 'framer-motion';
import { 
  Code, Database, Lock, Globe, Cpu, Terminal, 
  FileCode, Server, Smartphone
} from 'lucide-react';
import { useAppContext } from '../../context/AppContext';

const About = () => {
  const { personalInfo, skillsData } = useAppContext();
  
  const technicalSkills = [
    {
      name: "Programming",
      icon: <FileCode className="h-5 w-5 text-primary" />, // Changed from Code to FileCode
      percentage: skillsData.technical[0].level
    },
    {
      name: "Database Management",
      icon: <Database className="h-5 w-5 text-primary" />, // Kept Database icon
      percentage: skillsData.technical[1].level
    },
    {
      name: "Security",
      icon: <Lock className="h-5 w-5 text-primary" />, // Kept Lock icon
      percentage: skillsData.technical[2].level
    },
    {
      name: "Web Development",
      icon: <Globe className="h-5 w-5 text-primary" />, // Changed from Terminal to Globe
      percentage: skillsData.technical[3].level
    },
    {
      name: "Android Development",
      icon: <Smartphone className="h-5 w-5 text-primary" />, // Changed from Cpu to Smartphone
      percentage: skillsData.technical[4].level
    },
    {
      name: "Software Development",
      icon: <Server className="h-5 w-5 text-primary" />, // Changed from Network to Server
      percentage: skillsData.technical[5].level
    }
  ];

  return (
    <section id="about" className="py-20 relative">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12 text-center"
        >
          <h2 className="text-3xl font-bold mb-2 terminal-text">
            <span className="text-primary">{'>'}</span> About Me
          </h2>
          <div className="w-20 h-1 bg-primary mx-auto"></div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="animated-border"
          >
            <div className="terminal-window">
              <div className="terminal-header">
                <div className="terminal-button terminal-button-red"></div>
                <div className="terminal-button terminal-button-yellow"></div>
                <div className="terminal-button terminal-button-green"></div>
                <div className="terminal-title">about.sh</div>
              </div>
              <div className="terminal-body">
                <div className="terminal-output">
                  <span className="terminal-prompt">$</span>{' '}
                  <span className="terminal-command">cat about.txt</span>
                  <div className="mt-4 space-y-4">
                    <p>{personalInfo.bio}</p>
                    <p>
                      To further deepen my knowledge and broaden my skill set, I am currently pursuing a degree in 
                      Information Technology at Fontys University of Applied Sciences. Here, I am expanding my expertise 
                      in areas such as software architecture, system development, and emerging technologies. I am always 
                      eager to explore new programming languages, frameworks, and best practices to stay ahead in the 
                      ever-evolving tech landscape.
                    </p>
                    <p>
                      I thrive on challenges and love building efficient, scalable, and user-friendly applications. 
                      Whether it's backend development, APIs, or new technologies, I am always ready to dive in and learn.
                    </p>
                    <p>Let's connect and create something amazing!</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-background/80 border border-border/50 rounded-lg p-6 shadow-lg hover:border-primary/30 transition-all duration-300 card-hover"
          >
            <h3 className="text-xl font-bold mb-6 terminal-text">
              <span className="text-primary">{'>'}</span> Technical Skills
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              {technicalSkills.map((skill, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: 0.1 * index }}
                  className="flex flex-col items-center"
                >
                  <div className="circular-progress mb-3">
                    <svg width="80" height="80">
                      <circle className="bg" cx="40" cy="40" r="36" />
                      <motion.circle
                        className="progress"
                        cx="40"
                        cy="40"
                        r="36"
                        strokeDasharray="226.2"
                        strokeDashoffset="226.2"
                        initial={{ strokeDashoffset: 226.2 }}
                        whileInView={{ 
                          strokeDashoffset: 226.2 - (226.2 * skill.percentage / 100) 
                        }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.5, delay: 0.5 }}
                      />
                    </svg>
                    <div className="percentage">{skill.percentage}%</div>
                  </div>
                  
                  <div className="flex items-center justify-center mb-2">
                    <div className="p-2 bg-primary/10 rounded-md">
                      {skill.icon}
                    </div>
                  </div>
                  
                  <h4 className="font-medium text-center">{skill.name}</h4>
                </motion.div>
              ))}
            </div>

            <div className="mt-8">
              <h3 className="text-xl font-bold mb-4 terminal-text">
                <span className="text-primary">{'>'}</span> Languages & Technologies
              </h3>
              <div className="flex flex-wrap gap-2">
                {[...skillsData.tools, ...skillsData.languages, ...skillsData.frameworks.slice(0, 5)].map((tech, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.05 * index }}
                    className="skill-tag"
                  >
                    {tech}
                  </motion.span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
